import { isLittleEndian } from "@/core/utils/endianness"
import { TagType } from "./tag-types"

type DataStreamOpts = {
	littleEndian?: boolean
}

export class DataStream {
	dataView: DataView
	byteView: Uint8Array
	littleEndian: boolean

	offset = 0

	textDecoder = new TextDecoder()
	textEncoder = new TextEncoder()

	constructor(source: DataView | ArrayBuffer = new ArrayBuffer(0), opts?: DataStreamOpts) {
		this.dataView = source instanceof ArrayBuffer ? new DataView(source) : source
		this.byteView = new Uint8Array(this.dataView.buffer)
		this.littleEndian = opts?.littleEndian ?? isLittleEndian()
	}

	setBuffer(buffer: ArrayBuffer) {
		this.dataView = new DataView(buffer)
		this.byteView = new Uint8Array(buffer)
		this.offset = 0
	}

	[TagType.BYTE] = {
		set: (value: number) => this.setUint8(value),
		get: () => this.getUint8(),
	};

	[TagType.ASCII] = {
		set: (value: number) => this.setUint8(value),
		get: () => this.getUint8(),
	};

	[TagType.SHORT] = {
		set: (value: number) => this.setUint16(value),
		get: () => this.getUint16(),
	};

	[TagType.LONG] = {
		set: (value: number) => this.setUint32(value),
		get: () => this.getUint32(),
	};

	[TagType.RATIONAL] = {
		set: (value: number) => {
			const [num, den] = DataStream.uRational(value)
			this.setUint32(num)
			this.setUint32(den)
		},
		get: () => this.getUint32() / this.getUint32(),
	};

	[TagType.SBYTE] = {
		set: (value: number) => this.setInt8(value),
		get: () => this.getInt8(),
	};

	[TagType.UNDEFINED] = {
		set: (value: number) => this.setUint8(value),
		get: () => this.getUint8(),
	};

	[TagType.SSHORT] = {
		set: (value: number) => this.setInt16(value),
		get: () => this.getInt16(),
	};

	[TagType.SLONG] = {
		set: (value: number) => this.setInt32(value),
		get: () => this.getInt32(),
	};

	[TagType.SRATIONAL] = {
		set: (value: number) => {
			const [num, den] = DataStream.sRational(value)
			this.setInt32(num)
			this.setInt32(den)
		},
		get: () => this.getInt32() / this.getInt32(),
	};

	[TagType.FLOAT] = {
		set: (value: number) => this.setFloat32(value),
		get: () => this.getFloat32(),
	};

	[TagType.DOUBLE] = {
		set: (value: number) => this.setFloat64(value),
		get: () => this.getFloat64(),
	}

	getUint8() {
		const value = this.dataView.getUint8(this.offset)
		this.offset += 1
		return value
	}

	getInt8() {
		const value = this.dataView.getInt8(this.offset)
		this.offset += 1
		return value
	}

	getUint16() {
		const value = this.dataView.getUint16(this.offset, this.littleEndian)
		this.offset += 2
		return value
	}

	getInt16() {
		const value = this.dataView.getInt16(this.offset, this.littleEndian)
		this.offset += 2
		return value
	}

	getUint32() {
		const value = this.dataView.getUint32(this.offset, this.littleEndian)
		this.offset += 4
		return value
	}

	getInt32() {
		const value = this.dataView.getInt32(this.offset, this.littleEndian)
		this.offset += 4
		return value
	}

	getFloat32() {
		const value = this.dataView.getFloat32(this.offset, this.littleEndian)
		this.offset += 4
		return value
	}

	getFloat64() {
		const value = this.dataView.getFloat64(this.offset, this.littleEndian)
		this.offset += 8
		return value
	}

	setUint8(value: number) {
		this.dataView.setUint8(this.offset, value)
		this.offset += 1
	}

	setInt8(value: number) {
		this.dataView.setInt8(this.offset, value)
		this.offset += 1
	}

	setUint16(value: number) {
		this.dataView.setUint16(this.offset, value, this.littleEndian)
		this.offset += 2
	}

	setInt16(value: number) {
		this.dataView.setInt16(this.offset, value, this.littleEndian)
		this.offset += 2
	}

	setUint32(value: number) {
		this.dataView.setUint32(this.offset, value, this.littleEndian)
		this.offset += 4
	}

	setInt32(value: number) {
		this.dataView.setInt32(this.offset, value, this.littleEndian)
		this.offset += 4
	}

	setFloat32(value: number) {
		this.dataView.setFloat32(this.offset, value, this.littleEndian)
		this.offset += 4
	}

	setFloat64(value: number) {
		this.dataView.setFloat64(this.offset, value, this.littleEndian)
		this.offset += 8
	}

	getText(length: number) {
		const subarray = this.byteView.subarray(this.offset, this.offset + length)
		this.offset += length
		return this.textDecoder.decode(subarray)
	}

	setText(text: string) {
		const data = this.textEncoder.encode(text)
		this.byteView.set(data, this.offset)
		this.offset += data.length
	}

	static sRational(x: number): [number, number] {
		if (x === 0) {
			return [0, 1]
		}

		const y = Math.abs(x)

		let dd: number

		if (y >= 32768.0) {
			dd = 1
		} else if (y >= 1.0) {
			dd = 32768
		} else {
			dd = 32768 * 32768
		}

		return [Math.round(x * dd), dd]
	}

	static uRational(x: number): [number, number] {
		if (x <= 0) {
			return [0, 1]
		}

		return DataStream.sRational(x)
	}
}
